import dayjs, { Dayjs } from 'dayjs'
import Logger from 'services/Logger'

import React, { useState, useEffect } from 'react'

import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import { eventsForReminders } from 'services/db/Queries/Event'
import { EventDoc, EventStatus } from 'types/api/changes'
import { Model } from 'types/model'
import { RRule, RRuleSet, rrulestr } from 'rrule'
import { mapRecurrency } from 'utils/api/events'
import { isTestEnv } from 'utils/env'
import Database from 'services/db/Database'
import { isTrulyEmpty } from 'utils/object'
import { t } from 'i18next'
import { isSameDay } from 'date-fns'
import { isCypress } from 'utils/cypress'
import { getDefaultTimezone } from 'index'
import { is24meApp } from 'utils/appType'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'

export function MinuteTimer({ children }: { children: React.ReactNode }) {
  const [currentTime, setCurrentTime] = useState<Date>(new Date())
  const [timerId, setTimerId] = useState<number | any | undefined>(undefined)

  function showEventAlertsIfNeeded() {
    const now = dayjs()

    try {
      UserSettings().then((userSettings) => {
        eventsForReminders(userSettings).then((events) => {
          events.forEach((groupcalEvent) => {
            const instances =
              groupcalEvent.Recurrence && groupcalEvent.Recurrence.Unit
                ? generateGroupcalArrayFromRecurrence(groupcalEvent)
                : [groupcalEvent]
            //going over all reminders

            if (
              groupcalEvent?.Reminder &&
              typeof groupcalEvent?.Reminder !== 'string'
            ) {
              groupcalEvent?.Reminder?.forEach((reminder) => {
                //per each instance
                instances.forEach((instance) => {
                  const parentStart = dayjs(
                    Number(groupcalEvent.StartDate) * 1000
                  )
                  let eventStart = dayjs
                    .unix(Number(instance.StartDate))
                    .set('hour', parentStart.hour())

                  if (instance.AllDay === '1') {
                    eventStart = dayjs
                      .unix(Number(instance.StartDate))
                      .tz('UTC')
                  }

                  const eventEnd = dayjs.unix(Number(instance.EndDate))
                  const minutesDiff = eventStart.diff(
                    now.tz(
                      instance.AllDay === '1' ? 'UTC' : getDefaultTimezone()
                    ),
                    'minute'
                  )

                  if (
                    (reminder.offset === 0 && minutesDiff === 0) ||
                    (reminder.offset != 0 &&
                      minutesDiff === reminder.offset / 60 - 1)
                  ) {
                    Logger.debug(
                      'will present notification for ',
                      groupcalEvent
                    )
                    const startText = eventStart.format('HH:mm')
                    const endText = eventEnd.format('HH:mm')

                    let secondLine = `${startText} - ${endText}`

                    if (groupcalEvent.AllDay === '1') {
                      secondLine = `${eventStart.format('MMM DD')}, ${t(
                        'allDay'
                      )}`
                    }

                    if (startText === endText) {
                      secondLine = `${startText}`
                    }

                    if (groupcalEvent.Location?.Address) {
                      secondLine += `\n${groupcalEvent.Location.Address}`
                    }
                    if (isTestEnv()) {
                      secondLine += `\n${process.env.REACT_APP_LINK_DOMAIN}`
                    }
                    showNotification(
                      groupcalEvent.Text ?? '',
                      secondLine,
                      groupcalEvent.id
                    )
                  }
                })
              })
            }
          })
        })
      })
    } catch (e) {
      //ignore minute update errors per https://24meapp.atlassian.net/jira/software/c/projects/SER/boards/15?view=detail&selectedIssue=SER-1308
    }
  }

  function launchEveryMinute() {
    Logger.pink('minute tick')
    try {
      showEventAlertsIfNeeded()
    } catch (e) {
      Logger.red('minute', e)
    }
  }

  useEffect(() => {
    const storedTimerId = LocalStorage.get('timerId')
    if (storedTimerId) {
      if (isCypress()) return
      setTimerId(parseInt(storedTimerId, 10))
    }

    return () => {
      if (timerId) {
        clearInterval(timerId)
        LocalStorage.remove('timerId')
      }
    }
  }, [timerId])

  useEffect(() => {
    if (!timerId) {
      const msUntilNextMinute =
        (60 - currentTime.getSeconds()) * 1000 - currentTime.getMilliseconds()

      const newTimer = setInterval(() => {
        if (isCypress()) return
        setCurrentTime(new Date())
      }, 60 * 1000)

      if (isCypress()) return
      setTimerId(newTimer)
      localStorage.setItem('timerId', newTimer.toString())

      setTimeout(() => {
        if (isCypress()) return
        setCurrentTime(new Date())
        clearInterval(newTimer)
        localStorage.removeItem('timerId')

        const nextTimer = setInterval(() => {
          if (isCypress()) return
          setCurrentTime(new Date())
          launchEveryMinute()
        }, 60 * 1000)

        if (isCypress()) return
        setTimerId(nextTimer)
        localStorage.setItem('timerId', nextTimer.toString())
        launchEveryMinute()
      }, msUntilNextMinute)
      launchEveryMinute()
    }
  }, [currentTime, timerId])

  return <>{children}</>
}

export function showNotification(title: string, body: string, tag: string) {
  if (typeof Notification === 'undefined') return
  try {
    Logger.blue('notif state', Notification.permission)
    const options = {
      body: body,
      icon: '/logo192.png',
      requireInteraction: true,
      tag: tag
    }
    if (Notification.permission === 'granted') {
      new Notification(title, options)
    } else if (Notification.permission !== 'denied') {
      Promise.resolve(Notification.requestPermission()).then(function (
        permission
      ) {
        if (permission === 'granted') {
          new Notification(title, options)
        } else {
          alert(title)
        }
      })
    }
  } catch (e) {
    console.error('no way to show notif', e)
  }
}

export function generateGroupcalArrayFromRecurrence(
  groupcalEvent: EventDoc,
  ignoreExclusions = false,
  from?: Dayjs,
  to?: Dayjs
): Model<EventDoc>[] | EventDoc[] {
  if (!groupcalEvent.Recurrence || typeof groupcalEvent.Recurrence === 'string')
    return [groupcalEvent]

  //return itself in
  if (groupcalEvent._id?.length === 0) return [groupcalEvent]
  try {
    const durInSeconds =
      Number(groupcalEvent.EndDate) - Number(groupcalEvent.StartDate)
    const rrule = rrulestr(
      mapRecurrency(
        groupcalEvent,
        true,
        (groupcalEvent.TimeZoneNameID?.length ?? 0) > 0
          ? groupcalEvent.TimeZoneNameID
          : getDefaultTimezone(),
        ignoreExclusions
      )
    )

    let instances = rrule.between(
      (from ?? dayjs(Number(groupcalEvent.StartDate) * 1000))
        .subtract(1, 'week')
        .startOf('day')
        .toDate(),
      (to ?? dayjs().startOf('day').add(365, 'day')).toDate()
    )

    const endRecurrence = groupcalEvent.Recurrence?.EndDate

    // we need to remove instances which are after the end date

    if (endRecurrence && !isTrulyEmpty(endRecurrence)) {
      instances = instances.filter((instance) => {
        return (
          groupcalEvent.AllDay === '1' ? dayjs.utc(instance) : dayjs(instance)
        ).isBefore(
          (groupcalEvent.AllDay === '1'
            ? dayjs.utc(instance)
            : dayjs(instance)
          ).endOf('day')
        )
      })
    }

    // Get the timezone offset in minutes
    let timezoneOffset = dayjs().tz(getDefaultTimezone()).utcOffset()

    if (groupcalEvent.AllDay === '1') {
      timezoneOffset = 0
    }

    // Adjust each instance by the timezone offset
    const instancesWithAdjustedTime = instances.map((instance) => {
      const instanceInUTC = dayjs(instance).utc()
      return instanceInUTC.subtract(timezoneOffset, 'minute').valueOf()
    })

    return instancesWithAdjustedTime.map((instance) => {
      const eventDoc = Database.toServerObject(groupcalEvent)
      const copyEvent: EventDoc = { ...groupcalEvent, ...eventDoc }
      let tz =
        (eventDoc.TimeZoneNameID?.length ?? 0) > 0
          ? eventDoc.TimeZoneNameID
          : getDefaultTimezone()

      if (
        is24meApp() &&
        copyEvent.AllDay === '1' &&
        copyEvent.TimeZoneNameID?.length === 0
      ) {
        tz = 'UTC'
      }

      copyEvent.StartDate = (
        dayjs(instance.valueOf()).tz(tz).valueOf() / 1000
      ).toString()
      copyEvent.EndDate = (
        dayjs(instance.valueOf()).tz(tz).add(durInSeconds, 'second').valueOf() /
        1000
      ).toString()
      if (typeof copyEvent.Recurrence?.Exclusion !== 'string') {
        copyEvent.Status =
          eventDoc.Status !== EventStatus.ACTIVE
            ? eventDoc.Status
            : String(
                findStatusForInstance(
                  copyEvent,
                  Number(copyEvent.StartDate) * 1000
                )
              )
      }

      return copyEvent
    })
  } catch (e) {
    return [groupcalEvent]
  }
}

export function findStatusForInstance(
  copyEvent: EventDoc,
  instanceTime: number
) {
  let maxStatus = Number(EventStatus.ACTIVE)
  const tz =
    (copyEvent.TimeZoneNameID?.length ?? 0) > 0
      ? copyEvent.TimeZoneNameID
      : getDefaultTimezone()

  for (const exclusion of copyEvent.Recurrence?.Exclusion ?? []) {
    const dayjsExclusion = dayjs(Number(exclusion.Date) * 1000).startOf('day')

    const dayjsEvent = dayjs(instanceTime).tz(tz)

    if (
      dayjsExclusion.format('YYYY-MM-DD') === dayjsEvent.format('YYYY-MM-DD')
    ) {
      if (Number(exclusion.Status) > maxStatus) {
        maxStatus = Number(exclusion.Status)
      }
    }
  }

  return maxStatus
}
